import type { Variant } from '@src/types';

const ALL_VALUE = '__all__';

/** A filterable dimension derived from variant data (size, season, etc.). */
export interface FilterDimension {
  key: string;
  label: string;
  /** Parallel array to variant.skus — value at index i is for skus[i]. */
  allValues: string[];
}

/**
 * Locate the "partnerCode_parentSku" prefix length within a SKU's underscore
 * segments. Partner codes aren't always a single segment (e.g. "ARC_CA"), so a
 * hardcoded length of 2 misidentifies the parentSku segment as a filter
 * dimension for those partners. Falls back to 2 if parentSku can't be located.
 */
const findBaseLength = (skuParts: string[], parentSku: string): number => {
  const parentSkuParts = parentSku.split('_');
  for (let i = 0; i <= skuParts.length - parentSkuParts.length; i++) {
    const matches = parentSkuParts.every(
      (part, j) => skuParts[i + j]?.toLowerCase() === part.toLowerCase(),
    );
    if (matches) return i + parentSkuParts.length;
  }
  return 2;
};

/**
 * Locate colorCode's segment range within a SKU.  colorCode can itself be
 * multi-segmented (e.g. "amaranthus_antique"), so comparing it against one
 * segment at a time misses it entirely. Instead this searches for colorCode as a
 * substring of the joined variable segments — the same shift the backend made, from
 * segment-count matching to content-based matching.
 */
const findColorRange = (
  variableParts: string[],
  colorCode: string,
): [start: number, end: number] | null => {
  const joined = variableParts.join('_').toLowerCase();
  const colorcode = colorCode.toLowerCase();
  const idx = joined.indexOf(colorcode);
  if (idx === -1) return null;

  const atStartBoundary = idx === 0 || joined[idx - 1] === '_';
  const matchEnd = idx + colorcode.length;
  const atEndBoundary =
    matchEnd === joined.length || joined[matchEnd] === '_';
  if (!atStartBoundary || !atEndBoundary) return null;

  const start = (joined.slice(0, idx).match(/_/g) || []).length;
  const end = start + colorcode.split('_').length;
  return [start, end];
};

/**
 * Build scope keys from variant/filter state. Determines what images are displayed
 * and where saves go. Color position is detected dynamically from the SKU structure.
 */
export function buildScopeKeys(
  selectedColorway: string,
  selectedVariant: Variant | null,
  selectedFilters: Record<string, string>,
  filterDimensions: FilterDimension[],
  parentSku: string,
): string[] {
  if (
    !selectedColorway ||
    !selectedVariant ||
    selectedVariant.skus.length === 0
  )
    return [];

  const firstSku = selectedVariant.skus[0];
  const skuParts = firstSku.split('_');
  const baseLength = findBaseLength(skuParts, parentSku);

  if (skuParts.length <= baseLength) {
    return [selectedColorway];
  }

  const variableParts = skuParts.slice(baseLength);
  const colorRange = findColorRange(variableParts, selectedVariant.colorCode);
  let colorStart = colorRange ? colorRange[0] : -1;
  let colorEnd = colorRange ? colorRange[1] : -1;

  // Last resort: a single-SKU colorway whose colorCode still can't be located
  // (e.g. it doesn't appear verbatim in the SKU at all). Treat the entire
  // remaining variable portion as the color rather than wildcarding it away —
  // otherwise the key collapses to "<base>_???", which matches and merges
  // images from every colorway on read.
  if (colorStart === -1 && selectedVariant.skus.length === 1) {
    colorStart = 0;
    colorEnd = variableParts.length;
  }

  // Build a map: non-color variable positions (in order) → filterDimensions (in order)
  const positionToDimIdx = new Map<number, number>();
  let dimCounter = 0;
  for (let vi = 0; vi < variableParts.length; vi++) {
    if (vi >= colorStart && vi < colorEnd) continue;
    if (dimCounter < filterDimensions.length) {
      positionToDimIdx.set(vi, dimCounter);
      dimCounter++;
    }
  }

  // Build the scope key segment by segment
  const keyParts = [...skuParts.slice(0, baseLength)];

  for (let vi = 0; vi < variableParts.length; vi++) {
    if (vi >= colorStart && vi < colorEnd) {
      keyParts.push(variableParts[vi]);
      continue;
    }

    const dimIdx = positionToDimIdx.get(vi);
    if (dimIdx === undefined) {
      keyParts.push('???');
      continue;
    }

    const dim = filterDimensions[dimIdx];
    const filterValue = selectedFilters[dim.key];

    if (filterValue && filterValue !== ALL_VALUE) {
      // Find a SKU with this display value to extract the code at this position
      const skuIndex = dim.allValues.findIndex(val => val === filterValue);
      if (skuIndex !== -1 && selectedVariant.skus[skuIndex]) {
        const matchParts = selectedVariant.skus[skuIndex].split('_');
        keyParts.push(matchParts[baseLength + vi]);
      } else {
        keyParts.push(filterValue);
      }
    } else {
      keyParts.push('???');
    }
  }

  return [keyParts.join('_')];
}

/**
 * Build scope keys for reading — determines what images to display.
 * Uses pattern matching: for each non-wildcard position in the scope key pattern,
 * the candidate key must have the same value or ???.
 */
export function buildScopeKeysForReading(
  scopeKeys: string[],
  matchingSkus: string[],
  selectedVariant: Variant | null,
  imagesByColorway: Record<string, Record<string, string>> | undefined,
  studioImagesByColorway: Record<string, Record<string, string>> | undefined,
  videosByColorway?: Record<string, Record<string, string>>,
): string[] {
  if (!selectedVariant || selectedVariant.skus.length === 0) return scopeKeys;

  const keys = new Set<string>(scopeKeys);

  if (matchingSkus.length > 0) {
    matchingSkus.forEach(sku => keys.add(sku));
  } else {
    const patternParts = scopeKeys[0]?.split('_');
    if (patternParts) {
      const existingPartnerKeys = Object.keys(imagesByColorway || {});
      const existingStudioKeys = Object.keys(studioImagesByColorway || {});
      const existingVideoKeys = Object.keys(videosByColorway || {});

      for (const key of [
        ...existingPartnerKeys,
        ...existingStudioKeys,
        ...existingVideoKeys,
      ]) {
        const keyParts = key.split('_');
        if (keyParts.length !== patternParts.length) continue;

        let matches = true;
        for (let i = 0; i < patternParts.length; i++) {
          if (patternParts[i] === '???') continue;
          if (keyParts[i] === '???') continue;
          if (patternParts[i] !== keyParts[i]) {
            matches = false;
            break;
          }
        }
        if (matches) keys.add(key);
      }
    }
  }

  return Array.from(keys);
}
